import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, forkJoin, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CustomizerSettingsService } from '../../../../customizer-settings/customizer-settings.service';
import { LoadingService } from '../../../../services/loading.service';
import { ObjectUtility } from '../../../../utilities/ObjectUtility';
import { whitespaceValidator } from '../../../../validators/ValidationService';
import { CommonModule } from '@angular/common';
import { BaseSearchOptions } from '../../../../models/core/table/BaseSearchOptions';
import { StateService } from '../../../../services/state.service';
import { City } from '../../../../models/domain/City';
import { State } from '../../../../models/domain/State';
import { CityService } from '../../../../services/city.service';

@Component({
  selector: 'app-city-details',
  templateUrl: './city-details.component.html',
  styleUrl: './city-details.component.scss',
  imports: [ReactiveFormsModule, CommonModule],
})

export class CityDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() classApplied = new EventEmitter<boolean>();
  @Output() saveConfirmationEvent = new EventEmitter<boolean>();
  @Input() data!: City;
  // isToggled
  isToggled = false;
  frmGroup!: FormGroup;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  public cityOriginal = new City;
  public city: City = new City;
  public titleText = "";
  public titleButton = "";
  public errorMessage = "";
  public currentCityId = 0;
  isSubmitted = false;

  //dropdown DataBind
  public state$!: Observable<State[]>;
  public state!: State[];
  public pageIndex = 0;
  public pageSize = 1000;
  public searchOptions!: BaseSearchOptions;

  constructor(
    public themeService: CustomizerSettingsService,
    private formBuilder: FormBuilder,
    private cityService: CityService,
    private toasterService: ToastrService,
    private loadingService: LoadingService,
    private stateService: StateService,

  ) {
    this.initialize();
  }

  public ngOnInit(): void {
    this.getDropdownData();
    if (this.data) {
      this.currentCityId = this.data.id ? this.data.id : 0;
      this.city = ObjectUtility.deepClone(this.data);
      this.cityOriginal = ObjectUtility.deepClone(this.data);
      this.setForm(this.city);
    }
    else {
      this.currentCityId = 0;
      const newCity = new City();
      this.city = ObjectUtility.deepClone(newCity);
      this.cityOriginal = ObjectUtility.deepClone(newCity);
      this.setForm(this.city);
    }
  }

  private initialize(): void {
    this.themeService.isToggled$.subscribe(isToggled => {
      this.isToggled = isToggled;
    });

    this.searchOptions = new BaseSearchOptions(undefined, 'name', 0, this.pageIndex, this.pageSize);
    this.isSubmitted = false;
    this.createForm();
  }

  public ngAfterViewInit(): void {

    this.frmGroup.controls['stateNameControl'].valueChanges.subscribe(value => {
      this.city.state = value;
    });

    this.frmGroup.controls['nameControl'].valueChanges.subscribe(value => {
      this.city.name = value;
    });

  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get f() { return this.frmGroup.controls; }

  private createForm(): void {
    this.frmGroup = this.formBuilder.group({
      nameControl: new FormControl('', [Validators.required, whitespaceValidator()]),
      stateNameControl: new FormControl(null, [Validators.required]),

    })
  }

  private setForm(city: City): void {
    if (city) {
      this.frmGroup.patchValue({
        nameControl: city.name,
        stateNameControl: city?.state,

      });
    }
    this.titleText = `${this.currentCityId === 0 ? "Add" : "Edit"} City`;
    this.titleButton = `${this.currentCityId === 0 ? "Create" : "Update"}`;
  }

  public onSave(): void {
    this.isSubmitted = true;
    if (this.frmGroup.valid) {
      this.loadingService.showLoading();
      this.addEditCity();
    }
  }

  private addEditCity(): void {
    if (this.city?.id === 0) {
      this.cityService.create(this.sanitizeService(this.city))
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            this.loadingService.hideLoading();

            return of(null);
          }),
          finalize(() => {
            this.loadingService.hideLoading();
          }),
          takeUntil(this.destroy$))
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.city = response?.body != null ? response?.body : new City;
            this.saveConfirmationEvent.emit(true);
            this.toggleClass();
            this.toasterService.success("Save successfully");
          }
          this.loadingService.hideLoading();
        });
    } else {
      this.cityService.update(this.sanitizeService(this.city))
        .pipe(
          catchError((err: any) => {
            if (err && err.error) {
              this.isSubmitted = false;
              if (err.error.isCustom) {
                this.errorMessage = err.error.message;
                this.toasterService.warning(err.error.message);
              } else {
                this.toasterService.error('Something went wrong!');
              }
            } else {
              this.isSubmitted = false;
              this.toasterService.error('Something went wrong!');
            }
            return of(null);
          }),
          takeUntil(this.destroy$),
          // Ensure loading is always hidden, even if error is not caught
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(response => {
          if (response) {
            this.isSubmitted = false;
            this.city = response?.body != null ? response?.body : new City;
            this.saveConfirmationEvent.emit(true);
            this.toasterService.success("Update successfully");
            this.toggleClass();
          }
        });
    }
  }


  public toggleClass(): void {
    this.classApplied.emit(false);
  }

  public dataHasChanged(): boolean {
    const baseEqual = ObjectUtility.deepEqual(this.city, this.cityOriginal);
    return !baseEqual;
  }

  public formIsValid(): boolean {
    const isValid = this.frmGroup.controls['stateNameControl'].valid && this.frmGroup.controls['nameControl'].valid
    return isValid;
  }

  public stateNameComparer(t1: City, t2: City): boolean {
    return t1 && t2 ? (t1.id === t2.id || t1.name === t2.name) : t1 === t2;
  }

  private sanitizeService(city: City): City {
    city.state = null;
    city.stateId = this.frmGroup.controls['stateNameControl'].value.id;

    return city;
  }

  private getDropdownData() {
    this.loadingService.showLoading();

    this.state$ = this.stateService.get(this.searchOptions).pipe(
      map((result: any) => {
        this.loadingService.hideLoading();
        return result.body.values
      }),
      catchError(() => {
        this.loadingService.hideLoading();
        return of([])
      })
    );

    /*const state = this.stateService.get(this.searchOptions);
    forkJoin([state])
      .pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
        if (result) {
          this.state = result[0].body.values;
          this.setForm(this.state);
          this.loadingService.hideLoading();
        }
      });*/
  }

}